import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel
} from "@tanstack/react-table";
import {useEffect, useMemo, useState, useCallback} from "react";
import {Input} from "../inputs/index.js";
import {Pagination} from "./Pagination.jsx";
import {TableBase} from "./TableBase.jsx";
import PropTypes from "prop-types";
import ButtonWithIcon from "../ButtonWithIcon.jsx";
import {Icons} from "../Icons.jsx";
import SelectColumns from "./SelectColumns.jsx";
import ExportTableDataSelect from "./ExportTableDataSelect.jsx";

export const SimpleTable = (props) => {
    const urlPath = window.location.pathname;
    const detailKey = props.detailKey || '';
    const sortingKey = `sorting_${urlPath}${detailKey}`;
    const paginationKey = `pagination_${urlPath}${detailKey}`;
    const globalFilterKey = `globalFilter_${urlPath}${detailKey}`;
    const columnFiltersKey = `columnFilters_${urlPath}${detailKey}`;
    const columnVisibilityKey = `columnVisibility_${urlPath}${detailKey}`;

    const storedSorting = JSON.parse(localStorage.getItem(sortingKey)) || [
        {
            id: props.initialSortingColumn?.id || 'id',
            [props.initialSortingColumn?.sort || 'asc']: true
        }
    ];
    const storedGlobalFilter = localStorage.getItem(globalFilterKey) || '';
    const storedColumnFilters = JSON.parse(localStorage.getItem(columnFiltersKey)) || [];
    const storedColumnVisibility = JSON.parse(localStorage.getItem(columnVisibilityKey)) || {};
    const storedPagination = !props.hidePagination ? JSON.parse(localStorage.getItem(paginationKey)) || {
        pageSize: 10,
        pageIndex: 0
    } : {
        pageSize: 100000,
        pageIndex: 0
    }

    const [globalFilter, setGlobalFilter] = useState(storedGlobalFilter);
    const [columnFilters, setColumnFilters] = useState(storedColumnFilters);
    const [sortings, setSortings] = useState(storedSorting);
    const [columnVisibility, setColumnVisibility] = useState(storedColumnVisibility);
    const [pagination, setPagination] = useState(storedPagination);

    useEffect(() => {
        setPagination(p => ({ ...p, pageIndex: 0 }))
    }, [columnFilters, globalFilter])

    const query = !!props.queryBuilder && props.queryBuilder(
        columnFilters?.map(f => {
            const column = props.columns.find(c => c.accessorKey === f.id);
            return {
                ...f,
                field: column?.meta?.filterSearch || f.id,
                id: column?.meta?.filterSearch || f.id,
                type: column?.meta?.filterOperator || 'like',
            };
        }),
        pagination.pageIndex + 1,
        pagination.pageSize,
        {
            id: props.columns.find(f => f.accessorKey === sortings[0]?.id)?.meta?.filterSearch ||
                props.columns.find(f => f.accessorKey === sortings[0]?.id)?.accessorKey,
            desc: sortings[0]?.desc
        }
    );

    const memoizedData = useMemo(() => props.data?.length ? props.data : (props.data?.list || []), [props.data]);
    const memoizedColumnDef = useMemo(() => props.columns, [props.columns]);

    const updateLocalStorage = useCallback(() => {
        localStorage.setItem(sortingKey, JSON.stringify(sortings));
        localStorage.setItem(globalFilterKey, globalFilter);
        localStorage.setItem(columnFiltersKey, JSON.stringify(columnFilters));
        localStorage.setItem(paginationKey, JSON.stringify(pagination));
        localStorage.setItem(columnVisibilityKey, JSON.stringify(columnVisibility));

        if (props.setQuery) {
            props.setQuery(query);
        }
        //      eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortings, globalFilter, columnFilters, pagination, columnVisibility, query]);

    useEffect(() => {
        updateLocalStorage();
    }, [updateLocalStorage]);

    const tableInstance = useReactTable({
        columns: memoizedColumnDef,
        data: memoizedData,
        rowCount: props.data?.totalCount || props.data?.length,
        getSortedRowModel: getSortedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: !!props.queryBuilder,
        manualSorting: !!props.queryBuilder,
        manualFiltering: !!props.queryBuilder,
        onPaginationChange: setPagination,
        onColumnVisibilityChange: setColumnVisibility,
        initialState: {
            sorting: storedSorting,
            pagination: {
                pageSize: !props.hidePagination ? (storedPagination.pageSize || 10) : 100000,
                pageIndex: !props.hidePagination ? (storedPagination.pageIndex || 0) : 0,
            }
        },
        state: {
            sorting: sortings,
            globalFilter,
            onGlobalFilterChange: setGlobalFilter,
            columnFilters,
            pagination,
            columnVisibility,
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSortings,
    });

    return (
        <>
            <div className={'d-flex justify-content-between align-items-center mt-3 mb-2'}>
                {!props.hideGlobalFilter && (
                    <div className="w-25 mb-2 d-flex justify-content-between">
                        {!props.queryBuilder && (
                            <Input
                                size="sm"
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                value={globalFilter}
                                placeholder="Szukaj..."
                            />
                        )}
                        {(globalFilter?.length > 0 || columnFilters?.length > 0) && (
                            <div style={{minWidth: `150px`}}>
                                <ButtonWithIcon
                                    variant={'warning'}
                                    title={'Wyczyść wszystkie filtry'}
                                    size={'sm'}
                                    text={'Wyczyść filtry'}
                                    icon={<Icons.close/>}
                                    onClick={() => {
                                        setColumnFilters([]);
                                        setGlobalFilter('');
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
                <div className={'d-flex gap-2 align-items-center'}>
                    {!props.hideColumnVisibility && <SelectColumns tableInstance={tableInstance} />}
                    {!props.hideExport && <ExportTableDataSelect tableInstance={tableInstance} fileName={props.exportFileName}/>}
                </div>
            </div>


            <div
                className={props.sticky !== false ? 'overflow-auto simpletable-scroll-hide' : ''}
                style={props.sticky !== false ? { maxHeight: 'calc(100vh - 220px)' } : undefined}
            >
                <TableBase
                    sticky={props.sticky !== false}
                    isFiltering={columnFilters.length > 0}
                    clearColumnFilters={() => setColumnFilters([])}
                    hideSorting={props.hideSorting}
                    addTh={props.addTh}
                    addTd={props.addTd}
                    tableInstance={tableInstance}
                />
            </div>

            {!props.hidePagination && <Pagination isServer={!!props.queryBuilder} tableInstance={tableInstance}/>}
        </>
    );
};

SimpleTable.propTypes = {
    data: PropTypes.array,
    columns: PropTypes.array,
    addTh: PropTypes.func,
    addTd: PropTypes.func,
    initialSortingColumn: PropTypes.oneOf([PropTypes.object, PropTypes.func]),
    initialPagination: PropTypes.oneOf([PropTypes.object, PropTypes.func]),
    hidePagination: PropTypes.bool,
    hideSorting: PropTypes.bool,
    hideGlobalFilter: PropTypes.bool,
    detailKey: PropTypes.string,
    defaultSort: PropTypes.object,
    setQuery: PropTypes.func,
    queryBuilder: PropTypes.func,
    hideColumnVisibility: PropTypes.bool,
    hideExport: PropTypes.bool,
    exportFileName: PropTypes.string,
    sticky: PropTypes.bool,
};
